import round from 'lodash/round';
import pluralize from 'pluralize';

import { HHMMSS_REGEX_SINGLE } from '../validate';

// TODO write tests to make sure this doesn't break
export const secondsToVideoTS = (
  secondsToConvert = 0,
  precision?: number
): string => {
  if (precision < 0) {
    throw new Error('secondsToVideoTS precision must be 0 or greater');
  }

  if (precision != null) {
    secondsToConvert = round(secondsToConvert, precision);
  }

  const hours = Math.floor(secondsToConvert / 3600);
  const minutes = Math.floor((secondsToConvert - hours * 3600) / 60);
  const seconds = secondsToConvert - hours * 3600 - minutes * 60;

  let timestamp = '';

  if (hours > 0) {
    timestamp += `${hours}:`.padStart(3, '0');
  }

  if (minutes > 0) {
    if (hours === 0) {
      // [m]m:ss
      timestamp += `${minutes}:`;
    } else {
      timestamp += `${minutes}:`.padStart(3, '0');
    }
  } else if (hours > 0) {
    // hh:00:ss
    timestamp += '00:';
  } else {
    // 0:ss
    timestamp += '0:';
  }

  timestamp += seconds.toString().padStart(2, '0');

  if (precision != null) {
    const timeParts = timestamp.split(':');
    const onlySecs = Number(timeParts[timeParts.length - 1]);

    if (precision === 0) {
      timeParts[timeParts.length - 1] = onlySecs
        .toFixed(precision)
        .padStart(2, '0');
    } else {
      timeParts[timeParts.length - 1] = onlySecs
        .toFixed(precision)
        .padStart(precision + 3, '0');
    }

    return timeParts.join(':');
  }

  return timestamp;
};

// similar to above function, but this is always HH:MM:SS regardless of how short/long
// also this function rounds the seconds to nearest second, unlike above function
export const secondsToLongVideoTS = (secondsToConvert = 0): string => {
  const hours = Math.floor(secondsToConvert / 3600);
  const minutes = Math.floor((secondsToConvert - hours * 3600) / 60);
  const seconds = Math.round(secondsToConvert - hours * 3600 - minutes * 60);

  let timestamp = '';

  if (hours < 10) {
    // 0h:mm:ss
    timestamp += '0';
  }

  // 0m
  timestamp += `${hours}:`;

  if (minutes < 10) {
    // hh:0m:ss
    timestamp += `0`;
  }

  timestamp += `${minutes}:`;

  if (seconds < 10) {
    // hh:mm:0s
    timestamp += `0`;
  }

  timestamp += seconds;

  return timestamp;
};

function normalizeTimeUnit(numberString) {
  if (isNaN(Number(numberString))) {
    throw 'Invalid timestamp provided';
  }

  return numberString.length === 1 ? `0${numberString}` : numberString;
}

export const hhMmSsToSeconds = (timeString: string): number => {
  let timeValues = timeString.split(':');

  if (timeValues.length < 3) {
    timeValues = timeValues
      .map(normalizeTimeUnit)
      .reduce((acc, curr) => `${acc}:${curr}`, '00')
      .split(':');
  } else if (!HHMMSS_REGEX_SINGLE.test(timeString)) {
    throw `Invalid timestamp provided: ${timeString}`;
  }

  // https://stackoverflow.com/a/9640384/1933131
  return (
    Number(timeValues[0]) * 60 * 60 +
    Number(timeValues[1]) * 60 +
    Number(timeValues[2])
  );
};

export const shorthandTimestampMomentConfig = (): {
  relativeTime: { [key: string]: string };
} => {
  return {
    relativeTime: {
      future: 'in %s',
      past: '%s ago',
      s: 'now',
      ss: '%ss',
      m: '1m',
      mm: '%dm',
      h: '1h',
      hh: '%dh',
      d: '1d',
      dd: '%dd',
      M: '1M',
      MM: '%dM',
      y: '1Y',
      yy: '%dY',
    },
  };
};

export function secondsToHumanReadableString(seconds: number): string {
  const numYears = Math.floor(seconds / 31536000);
  const numDays = Math.floor((seconds % 31536000) / 86400);
  const numHours = Math.floor(((seconds % 31536000) % 86400) / 3600);
  const numMinutes = Math.floor((((seconds % 31536000) % 86400) % 3600) / 60);
  const numSeconds = (((seconds % 31536000) % 86400) % 3600) % 60;

  let humanReadableString = '';

  if (numYears) {
    humanReadableString += `${Math.trunc(numYears)} ${pluralize(
      'year',
      numYears
    )} `;
  }

  if (numDays) {
    humanReadableString += `${Math.trunc(numDays)} ${pluralize(
      'day',
      numDays
    )} `;
  }

  if (numHours) {
    humanReadableString += `${Math.trunc(numHours)} ${pluralize(
      'hour',
      numHours
    )} `;
  }

  if (numMinutes) {
    humanReadableString += `${Math.trunc(numMinutes)} min `;
  }

  if (seconds < 60 && numSeconds) {
    humanReadableString += `${Math.trunc(numSeconds)} sec`;
  }

  return humanReadableString;
}

export const HOUR_IN_SECONDS = 3600;
export const DAY_IN_SECONDS = 24 * HOUR_IN_SECONDS;

export const isToday = (startDate: string | Date): boolean => {
  const today = new Date();

  startDate = new Date(startDate);

  return (
    startDate.getDate() == today.getDate() &&
    startDate.getMonth() == today.getMonth() &&
    startDate.getFullYear() == today.getFullYear()
  );
};

const MS_IN_DAY = 1000 * 3600 * 24;

export const daysAgo = (dateString: string | Date): number => {
  const msDiff = new Date().getTime() - new Date(dateString).getTime();

  return Math.floor(msDiff / MS_IN_DAY);
};
